// Imports
// *****************************************************
import {
  useEffect, useLayoutEffect, useRef,
} from 'react';
import { HeaderInterface } from 'src/types/header-types';
import { useInView } from 'react-intersection-observer';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import { TopBannersHub } from '@components/features/banners';
import { SmartAppBanner } from '@components/features/smart-app-banner/SmartAppBanner';
import { MainNavbar } from './MainNavbar/MainNavbar';
import { useHeaderInfoData } from './MainNavbar/data/useHeaderInfoData';
import { setMenuTopOffset } from './MainNavbar/config';


/**
 * @description Header component. If you want to render banners that are not sticky, just place them
 * outside the header tag. Anything placed inside the header tag will be sticky.
 * * **navbarTheme** - the theme of the navbar. Can be 'default' or 'transparent' atm.
 *
 * @example
 * // Place a non-sticky alert or banner at the top of the page.
 * // It will not scroll down with the header.
 * <>
 *   <Banner />
 *   <Header ... />
 * </>
 *
 * // Place a sticky alert or sticky banner. It will scroll down with the header.
 * <Header ... >
 *   <AlertMessage />
 *   <MainNavbar />
 * </Header>
 *
 * // Place a search bar inside the header
 * <Header ... >
 *   <MainNavbar />
 *   <SearchBar />
 * </Header>
 *
 */
export const Header = (props: HeaderInterface) => {
  // Main component props with default values
  const { navbarTheme, navbarType = 'main', withoutAlert } = props;

  const headerRef = useRef<HTMLDivElement>(null);
  const dummyHeaderRef = useRef<HTMLDivElement>(null);
  // Detect if the header is pinned (stuck) or not
  // This hook also creates a ref for the pinned element, in our case the header. If needed, you can merge
  // the refs using the 'mergeRefs' function
  // *********************************************************************
  const [pinnedRef, notPinned] = useInView({
    threshold: 1,
  });

  // Need to use a setter since we have two refs attached to the header
  // One is for the intersection observer, and the other is for the header element itself.
  const setRefs = (node: HTMLDivElement | null) => {
    headerRef.current = node;
    pinnedRef(node);
  };


  // Get current route; needed to highlight the current route in the menu for React pages
  // Get current locale
  // *********************************************************************
  const { pathname, locale } = useExtendedRouter();

  const hideBanners = withoutAlert;
  const hideBannersOnMobile = false;

  // Get the header data
  // The data is not language dependent, but I agreed with Levi that it's safer to do it anyway,
  // because the data is not that big, and it's not like the users are changing the language every minute.
  // *********************************************************************
  const {
    headerInfoData, headerInfoLoading, headerInfoValidating, isSessionAuthenticated,
  } = useHeaderInfoData(locale);

  // MainNavbar props. Just destructure it on component
  // *********************************************************************
  const mainNavbarProps = {
    headerInfo: headerInfoData,
    headerInfoLoading: headerInfoLoading && !isSessionAuthenticated,
    headerInfoValidating,
    navbarTheme,
    currentRoute: pathname,
    notPinned,
    locale,
  };

  // Set the dummy header height to the height of the fixed header, so none of the pages are affected since previously it was using sticky.
  // useLayoutEffect is used here to ensure that the height is set before the first paint.
  useLayoutEffect(() => {
    if (!headerRef.current) return undefined;

    let animationFrameId: number | undefined;

    const setDummyHeaderHeight = () => {
      if (!dummyHeaderRef.current) return;

      const height = headerRef.current?.offsetHeight ?? 0;
      dummyHeaderRef.current.style.height = `${height}px`;
      document.documentElement.style.setProperty('--header-height', `${height}px`);
    };

    const scheduleDummyHeaderHeight = () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }

      animationFrameId = requestAnimationFrame(() => {
        animationFrameId = undefined;
        setDummyHeaderHeight();
      });
    };

    setDummyHeaderHeight();

    const observer = new ResizeObserver(scheduleDummyHeaderHeight);
    observer.observe(headerRef.current);

    return () => {
      observer.disconnect();
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      document.body.style.paddingTop = '';
      document.documentElement.style.removeProperty('--header-height');
    };
  }, []);

  // Calculate header height + offset for the menu. When having banners or alerts,
  // with show/hide conditions, add them to the dependency array.
  useEffect(() => {
    setMenuTopOffset();
  }, [headerInfoData]);


  // Render the Header
  // *********************************************************************
  return (
    <>
      <header id="header" ref={setRefs} className="fixed inset-x-0 -top-px z-10 print:hidden">
        <SmartAppBanner />
        {!hideBanners && <div className={hideBannersOnMobile ? 'hidden lg:block' : undefined}><TopBannersHub /></div>}

        {/* Main Navbar */}
        {navbarType === 'main' && <MainNavbar {...mainNavbarProps} />}
      </header>
      {/* Dummy header for replicating header height to not alter any of the layouts by switching to a fixed header */}
      <div ref={dummyHeaderRef} className="print:hidden" />
    </>
  );
};
